/**
 * domains/ai/thesis2/prompt.ts — MER Algo Footprints v7.1H
 *
 * Production-grade prompt system for the Structured AI Thesis Engine.
 *
 * Exports:
 *   SYSTEM_PROMPT              — static system prompt (set once per session)
 *   buildUserPayload()         — dynamic user message from runtime evidence
 *   buildRepairPayload()       — retry payload for malformed JSON
 *   buildAIMessages()          — convenience: returns { system, messages[] }
 *
 * Design principles:
 *   - System prompt is static and token-efficient — does not change per call
 *   - User payload carries all dynamic evidence — no logic in system prompt
 *   - Gate results are explicitly surfaced — model cannot ignore them
 *   - Repair prompt re-uses the schema without re-sending all evidence
 *   - No markdown, no prose, no free commentary permitted in output
 */

import { ThesisEvidenceInput, StructuredThesis } from './types';
import { GateEvaluation } from './gates';
import { NormalizedEvidencePayload } from './evidence';

// ─────────────────────────────────────────────────────────────────────────────
// 1. SYSTEM PROMPT
//    Static — set once. Defines the engine's identity, output contract,
//    all enum values, consistency rules, and prohibited behaviours.
//    Kept under ~800 tokens for budget efficiency.
// ─────────────────────────────────────────────────────────────────────────────

export const SYSTEM_PROMPT = `\
You are the MER Algo Footprints Thesis Engine — a structured decision-support synthesis layer.

IDENTITY
You synthesize normalized market engine outputs into a typed thesis object.
You are NOT a narrator. You are NOT a financial advisor. You do NOT trade.
You produce a single raw JSON object and nothing else.

OUTPUT CONTRACT
- Return one raw JSON object. No markdown. No prose. No code fences. No commentary outside the JSON.
- Every field in the schema is required unless marked nullable.
- Do not add fields that are not in the schema.
- Do not omit required fields.

ENUMS — use EXACTLY these values, case-sensitive:

thesis_state:   ACTIONABLE | DEVELOPING | WAIT | HIGH_RISK | INVALIDATED
action_posture: LONG | SHORT | NEUTRAL | REDUCE_RISK | NO_TRADE
bias:           bullish | bearish | neutral | mixed
setup_classification: breakout_long | breakout_short | reversion_long | reversion_short |
                      continuation_long | continuation_short |
                      sweep_reversal_long | sweep_reversal_short | wait | unknown

CONSISTENCY RULES — these are hard constraints:
1. INVALIDATED → action_posture MUST be NO_TRADE
2. ACTIONABLE  → action_posture MUST NOT be NO_TRADE
3. LONG posture → bias MUST NOT be bearish (use mixed if genuinely conflicted)
4. SHORT posture → bias MUST NOT be bullish (use mixed if genuinely conflicted)
5. HIGH_RISK → action_posture SHOULD be REDUCE_RISK or NO_TRADE
6. WAIT → action_posture SHOULD be NO_TRADE or NEUTRAL
7. conviction and actionability are 0–100 integers — never floats, never outside range

ACTIONABLE GATE — you MUST NOT output thesis_state=ACTIONABLE unless ALL are true:
a. entry_trigger is a specific price level or observable event (not "watch for" / "await")
b. invalidation_level is a specific price (not "if thesis fails" / "if breakdown occurs")
c. confirmation_needed is a specific observable condition
d. evidence layers are genuinely aligned — not merely partially supporting

DETERMINISTIC GATE OVERRIDE — if GATE_STATUS in the user payload includes failed gates,
you MUST NOT output thesis_state=ACTIONABLE regardless of your own read.
Downgrade to DEVELOPING, WAIT, or HIGH_RISK based on the specific failures provided.

RESTRAINT RULES:
- Prefer WAIT or DEVELOPING over forced ACTIONABLE
- Uncertainty is correct — never manufacture confidence
- conviction < 50 when evidence is weak or conflicted
- conviction 65–85 when evidence is genuinely clear
- conviction > 85 only with exceptional multi-layer confluence
- mixed bias only when directional evidence genuinely contradicts itself
  (low RVOL or low conviction does NOT make evidence "mixed" — keep direction, reduce conviction)

PRICE FIELDS:
- entry_price_hint, target_1, target_2 must be numeric strings (e.g. "142.50") or null
- Do not write ranges, zones, or descriptive text in price fields
- Do not invent price levels not derivable from supplied evidence
- If a price level is not clearly supported by evidence, use null

EVIDENCE DISCIPLINE:
- Use only facts in the supplied evidence block
- Do not reference indicators, patterns, or data not present in the evidence
- Do not invent setup names, regime labels, or trap types not in the evidence
- gamma_read: if no gamma data present, write exactly: "N/A — no options data"
- uncertainty_disclosure: state what cannot be known from this evidence — be honest

CHANGE DETECTION:
- If a prior thesis is supplied, explicitly identify what changed
- what_changed must list specific observable differences, not restate the current thesis
- If no prior thesis: list the primary signals driving this thesis
- change_type must match the most significant change detected`;

// ─────────────────────────────────────────────────────────────────────────────
// 2. JSON SCHEMA (embedded in user payload — concise reference)
// ─────────────────────────────────────────────────────────────────────────────

const SCHEMA_BLOCK = `OUTPUT SCHEMA (produce this exact structure):
{
  "thesis_state":           "<enum>",
  "action_posture":         "<enum>",
  "bias":                   "<enum>",
  "conviction":             <0-100>,
  "actionability":          <0-100>,
  "setup_classification":   "<enum>",
  "setup_name":             "<string — human-readable>",
  "summary":                "<2-3 sentence core read>",
  "decision":               "<1 sentence — what to do right now>",
  "entry_plan": {
    "entry_trigger":        "<specific price or event — not vague>",
    "entry_price_hint":     "<numeric string e.g. '142.50' | null>",
    "confirmation_needed":  "<specific observable condition>",
    "timing_note":          "<string | null>"
  },
  "risk_plan": {
    "invalidation_level":   "<specific price or event — not vague>",
    "invalidation_reason":  "<why that level kills the thesis>",
    "primary_risk_note":    "<top risk in plain terms>"
  },
  "targets": {
    "target_1":             "<numeric string | null>",
    "target_2":             "<numeric string | null>",
    "target_logic":         "<why those levels>"
  },
  "management": {
    "max_hold_note":          "<time or condition to exit regardless>",
    "scale_note":             "<string | null>",
    "re_entry_note":          "<string | null>",
    "uncertainty_disclosure": "<honest disclosure of what is unknown>"
  },
  "evidence": {
    "smc_read":           "<1-2 sentences>",
    "regime_read":        "<1-2 sentences>",
    "liquidity_read":     "<1-2 sentences>",
    "trap_read":          "<1-2 sentences>",
    "gamma_read":         "<1-2 sentences or 'N/A — no options data'>",
    "setup_read":         "<1-2 sentences>",
    "supporting_factors": ["<string>", "..."],
    "conflict_factors":   ["<string>", "..."],
    "key_drivers":        ["<string>", "<string>", "<string>"]
  },
  "change_detection": {
    "change_type":       "<enum>",
    "material_changes":  ["<specific change>", "..."],
    "what_changed":      ["<specific event or shift>", "..."]
  },
  "audit": {
    "prompt_version": "<string>",
    "evidence_hash":  "<string>"
  }
}`;

// ─────────────────────────────────────────────────────────────────────────────
// 3. USER PAYLOAD BUILDER
//    Dynamic — built per call. Carries evidence, gates, prior thesis.
//    Token-efficient: evidence is pre-summarized (not raw engine objects).
// ─────────────────────────────────────────────────────────────────────────────

export const PROMPT_VERSION = 'thesis2-v1.1';

export interface UserPayloadOptions {
  input:        ThesisEvidenceInput;    // kept for prior thesis / metadata
  payload:      NormalizedEvidencePayload; // structured evidence (primary input)
  gates:        GateEvaluation;
  evidenceHash: string;
}

function formatGateStatus(gates: GateEvaluation): string {
  const failed = gates.results.filter((r: {passed: boolean}) => !r.passed);
  if (gates.passed) return 'GATE_STATUS: ALL_PASSED — ACTIONABLE is permitted if evidence supports it.';

  const lines = [
    `GATE_STATUS: FAILED (${failed.length} of ${gates.results.length} gates failed)`,
    `thesis_state MUST NOT be ACTIONABLE. Downgrade to: ${gates.downgraded_to ?? 'DEVELOPING'}`,
    `Forced posture: ${gates.forced_posture ?? 'NEUTRAL'}`,
    `Failed gates:`,
    ...failed.map((g: {gate: string; note: string|null}) => `  - ${g.gate}: ${g.note ?? 'failed'}`),
  ];
  return lines.join('\n');
}

function formatPriorThesis(prior: StructuredThesis | null): string {
  if (!prior) {
    return 'PRIOR THESIS: none — this is a fresh analysis. what_changed should list the primary signals.';
  }
  return [
    `PRIOR THESIS:`,
    `  state=${prior.thesis_state} posture=${prior.action_posture} bias=${prior.bias}`,
    `  conviction=${prior.conviction} actionability=${prior.actionability}`,
    `  provenance=${prior.audit.validation_status}`,
    `  summary: ${prior.summary}`,
    `  invalidation: ${prior.risk_plan.invalidation_level}`,
    `Classify what materially changed. what_changed must list specific differences.`,
  ].join('\n');
}

export function buildUserPayload(opts: UserPayloadOptions): string {
  const { input, payload, gates, evidenceHash } = opts;

  // Serialize the structured evidence payload as compact JSON
  // This is the primary evidence block — structured, not prose
  const evidenceJSON = JSON.stringify(payload, null, 2);

  return [
    `SYMBOL: ${input.symbol}`,
    `TIMEFRAME: ${input.timeframe}`,
    `TIMESTAMP: ${new Date(input.timestamp).toISOString()}`,
    `PROMPT_VERSION: ${PROMPT_VERSION}`,
    `EVIDENCE_HASH: ${evidenceHash}`,
    '',
    formatGateStatus(gates),
    '',
    'STRUCTURED EVIDENCE PAYLOAD:',
    '```json',
    evidenceJSON,
    '```',
    '',
    formatPriorThesis(input.priorThesis),
    '',
    SCHEMA_BLOCK,
    '',
    'Respond with the JSON object only. No other text.',
  ].join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. REPAIR PROMPT
//    Sent as a follow-up when the first response fails validation.
//    Re-uses prior context without resending the full evidence block.
//    Includes the specific validation errors to guide correction.
// ─────────────────────────────────────────────────────────────────────────────

export function buildRepairPayload(
  priorRaw:         string,
  validationErrors: string[],
  symbol:           string,
  curPrice:         number,
): string {
  return [
    `Your previous response for ${symbol} failed validation.`,
    ``,
    `VALIDATION ERRORS:`,
    ...validationErrors.map(e => `  - ${e}`),
    ``,
    `PRIOR RESPONSE (malformed):`,
    priorRaw.slice(0, 1200),   // truncate to avoid token waste
    ``,
    `REPAIR INSTRUCTIONS:`,
    `1. Return ONLY the corrected JSON object — no prose, no code fences.`,
    `2. Fix every listed error.`,
    `3. Do not change fields that were not listed as errors.`,
    `4. Ensure all enum values match exactly (case-sensitive).`,
    `5. Ensure price fields are numeric strings (e.g. "142.50") or null.`,
    `6. Current price is ${curPrice.toFixed(2)} — price fields must be within 30% of this.`,
    `7. Maintain internal consistency: state / posture / bias must not conflict.`,
    ``,
    `Corrected JSON:`,
  ].join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. CONVENIENCE: buildAIMessages()
//    Returns the messages array ready for the Anthropic API.
//    Separates system from user — improves caching and reduces per-call cost.
// ─────────────────────────────────────────────────────────────────────────────

export interface AIMessages {
  system:   string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
}

export function buildAIMessages(opts: UserPayloadOptions): AIMessages {
  return {
    system:   SYSTEM_PROMPT,
    messages: [{ role: 'user', content: buildUserPayload(opts) }],
  };
}

// Re-export SCHEMA_BLOCK for use in schema.ts prompt embedding if needed
export { SCHEMA_BLOCK };

export function buildRepairMessages(
  originalMessages: AIMessages,
  malformedResponse: string,
  validationErrors:  string[],
  symbol:            string,
  curPrice:          number,
): AIMessages {
  return {
    system:   SYSTEM_PROMPT,   // same system — no need to resend
    messages: [
      ...originalMessages.messages,
      { role: 'assistant', content: malformedResponse },   // what the model sent
      { role: 'user',      content: buildRepairPayload(malformedResponse, validationErrors, symbol, curPrice) },
    ],
  };
}
